'use client'

import { createContext, use, useRef, useState } from 'react'

import { useMountEffect } from '@/hooks/useMountEffect'

const VizVisible = createContext(false)

// True while the surrounding <Viz> is at least 40% on screen. Demos run their
// animation loop only while this is true.
export function useVizVisible() {
  return use(VizVisible)
}

// The frame for a live demo. It tells its demo when it's on screen so nothing
// animates off screen.
export function Viz({
  label,
  wide = true,
  children,
}: {
  // Describes what the demo shows, for screen readers.
  label: string
  wide?: boolean
  children: React.ReactNode
}) {
  const ref = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  useMountEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!!entry?.isIntersecting),
      { threshold: 0.4 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  })

  return (
    <figure
      ref={ref}
      className={wide ? 'story-viz story-wide' : 'story-viz'}
      aria-label={label}
    >
      <VizVisible value={visible}>{children}</VizVisible>
    </figure>
  )
}
